import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDispatch } from 'src/store/useDispatch';
import { useSelector } from 'react-redux';

import PagePanel from 'src/components/PagePanel';
import { Table } from 'src/components/TableNew';
import PageHeader from 'src/components/PageHeader';
import NoResult from 'src/components/NoResult/NoResult';
import { FilterBar, type FilterDef } from 'src/components/FilterBar';
import { useFilterQueryEffect } from 'src/hooks/useFilterQueryEffect';
import { useResetPageParam } from 'src/hooks/useResetPageParam';
import { useAdminPermissions } from 'src/hooks/useAdminPermissions';
import { PERMISSIONS } from 'src/utils/permissions';
import { ROUTE_USERS } from 'src/utils/routes';
import { convertDate } from 'src/components/Table/helpers';
import { formatKycStatusLabel } from 'src/api/adminUsers';
import {
  formatKycUserName,
  formatProviderLabel,
  formatRiskScore,
  KycListItem,
} from 'src/api/adminKyc';
import { KYC_HEADERS, KYC_PROVIDER_OPTIONS } from 'src/utils/constants';
import { fetchKyc, updateFilter, resetFilters } from 'src/store/slice/kyc';

import KycDetailsModal from './KycDetailsModal';
import { PageTab, PageTabs, StatusBadge, UserLink } from './styled';

type KycTabId = 'all' | 'pending' | 'review' | 'verified' | 'rejected' | 'expired';

/**
 * Tabs map straight onto list filters. "Needs review" is the only queue an
 * admin has to work: it cuts across status, so it filters on the flag instead.
 */
const TABS: {
  id: KycTabId;
  label: string;
  status?: string;
  requiresManualReview?: boolean;
}[] = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending', status: 'PENDING' },
  { id: 'review', label: 'Needs review', requiresManualReview: true },
  { id: 'verified', label: 'Verified', status: 'VERIFIED' },
  { id: 'rejected', label: 'Rejected', status: 'CANCELED' },
  { id: 'expired', label: 'Expired', status: 'EXPIRED' },
];

// One bar per filter; the API ANDs them with the tab and the provider picker.
const KYC_FILTERS: FilterDef[] = [
  { key: 'name', label: 'Customer name', placeholder: 'First or "first last"' },
  { key: 'email', label: 'Email', placeholder: 'Starts with…' },
  { key: 'country_code', label: 'Country', placeholder: 'e.g. PK' },
  { key: 'provider', label: 'Provider', options: KYC_PROVIDER_OPTIONS, allLabel: 'All providers' },
  { key: 'id', label: 'Record ID', placeholder: 'KYC record id' },
];

const FIELDS_TO_SHOW = [
  'customer',
  'provider_label',
  'status_label',
  'review_label',
  'risk_label',
  'decision_label',
  'updated_label',
];

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  const { date, time } = convertDate(value);
  return `${date} - ${time}`;
};

const Kyc = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<KycTabId>('all');
  const [selectedKycId, setSelectedKycId] = useState<string | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const resetPageParam = useResetPageParam();
  const { can } = useAdminPermissions();
  const canOpenUser = can(PERMISSIONS.USER_READ);

  const {
    data: { kyc, totalCount, totalPages, filters },
    loading,
  } = useSelector(({ kyc }) => kyc);

  const tab = TABS.find((item) => item.id === activeTab) ?? TABS[0];
  const tabStatus = tab.status;
  const tabRequiresReview = tab.requiresManualReview;

  // The tab is part of the query: changing it re-runs from page 1, like a filter.
  const runQuery = useCallback(
    (params: { page: number; filters: Record<string, string> }) => {
      const { provider, ...rest } = params.filters;
      dispatch(
        fetchKyc({
          page: params.page,
          filters: rest,
          status: tabStatus,
          provider,
          requiresManualReview: tabRequiresReview,
        }),
      );
    },
    [dispatch, tabRequiresReview, tabStatus],
  );

  const { applied: appliedFilters, refresh: refreshQuery } = useFilterQueryEffect({
    filters,
    currentPage,
    setCurrentPage,
    query: runQuery,
  });

  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, [dispatch]);

  // Memoised so the open modal keeps a stable prop identity across the list
  // re-renders an alert or a refetch causes.
  const reload = useCallback(() => {
    runQuery({ page: currentPage, filters: appliedFilters });
  }, [appliedFilters, currentPage, runQuery]);

  const closeDetails = useCallback(() => setSelectedKycId(null), []);

  const handleTabChange = (next: KycTabId) => {
    if (next === activeTab) return;

    setActiveTab(next);
    setCurrentPage(1);
    // Pagination treats the URL as the source of truth, so local state alone
    // would be overwritten straight back to the previous tab's page.
    resetPageParam();
    dispatch(resetFilters());
  };

  const items = useMemo(
    () =>
      (kyc ?? []).map((record: KycListItem) => ({
        ...record,
        customer: formatKycUserName(record.user),
        provider_label: formatProviderLabel(record.provider),
        status_label: formatKycStatusLabel(record.status),
        review_label: record.requires_manual_review ? 'Needs review' : '—',
        risk_label: formatRiskScore(record.veriff?.risk_score),
        decision_label: formatDateTime(record.veriff?.decision_time),
        updated_label: formatDateTime(record.updated_at),
      })),
    [kyc],
  );

  return (
    <PagePanel>
      <PageHeader title={`KYC (${totalCount})`} hideSearch />

      <PageTabs>
        {TABS.map((item) => (
          <PageTab
            key={item.id}
            type="button"
            $active={activeTab === item.id}
            onClick={() => handleTabChange(item.id)}
          >
            {item.label}
          </PageTab>
        ))}
      </PageTabs>

      <FilterBar
        filters={KYC_FILTERS}
        values={filters}
        onChange={(key, value) => dispatch(updateFilter({ key, value }))}
        onClear={() => dispatch(resetFilters())}
        onRefresh={refreshQuery}
      />

      {items.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={KYC_HEADERS}
          items={items}
          columns="1.4fr 0.8fr 0.9fr 1fr 0.6fr 1.1fr 1.1fr"
          fieldsToShow={FIELDS_TO_SHOW}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          onRowClick={(item) => {
            const rowId = String(item.id ?? item._id ?? '');
            if (rowId) setSelectedKycId(rowId);
          }}
          renderCell={(item, cellField) => {
            if (cellField === 'customer') {
              const label = String(item.customer || '—');
              const ownerId = item.user_id || item.user?.id;
              if (!ownerId || !canOpenUser) return <span title={item.user?.email}>{label}</span>;
              return (
                <UserLink
                  type="button"
                  title={item.user?.email ? `${label} (${item.user.email})` : label}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${ROUTE_USERS}/${ownerId}`);
                  }}
                >
                  {label}
                </UserLink>
              );
            }
            if (cellField === 'status_label') {
              return (
                <StatusBadge $status={String(item.status || '')}>{item.status_label}</StatusBadge>
              );
            }
            if (cellField === 'review_label') {
              if (!item.requires_manual_review) return <span>—</span>;
              return <StatusBadge $status="needs review">Needs review</StatusBadge>;
            }
            if (cellField === 'provider_label') {
              return <StatusBadge $status={String(item.provider || '')}>{item.provider_label}</StatusBadge>;
            }
            return null;
          }}
        />
      ) : (
        <NoResult />
      )}

      {selectedKycId ? (
        <KycDetailsModal kycId={selectedKycId} onClose={closeDetails} onUpdated={reload} />
      ) : null}
    </PagePanel>
  );
};

export default Kyc;
